import tracking from '@g4/g4-analytics';
import moment from '@g4/moment-utc';
import getAncillaryRawProduct from '../ancillariesPage/getAncillaryRawProducts';
import {
  sortOrder,
  tripTypeValues,
} from '../../utils/constants/trackingConstants';
import { ALLEGIANT_FIRST, ALLEGIANT_FIRST_BUNDLE } from '../indexPage/constants';

const getSeatName = (seatMap, seatRow, seatColumn) => {
  const seatRowList = seatMap.rows.find(row => row.id === seatRow).items;
  const sizeId = seatRowList.find(seat => seat.id === seatRow + seatColumn)
    .sizeId;
  return seatMap.seatSizesMap.find(
    seatSize => seatSize.id === sizeId.toString()
  ).name;
};

/**
 * Function to get the seatMap based on the seat segment
 * @param {String} seatDirection - Direction of the seat segment
 * @param {Object} seatMaps - seatMap information
 * @returns {Object} - seatMap of individual segment
 */
const getSeatMap = (seatDirection, seatMaps) =>
  seatDirection === 'D' ? seatMaps.departing : seatMaps.returning;

export const getRawProducts = (
  seatsList,
  flightSearchCriteria,
  activeLeg,
  seatMaps,
  operatedBy,
  affinityString
) => {
  const sortedSeatsList = seatsList?.sort((a, b) =>
    moment(a.flightId.split('-G4-')[0]).diff(
      moment(b.flightId.split('-G4-')[0])
    )
  );

  const seatDirectionList = sortedSeatsList.map(seat => {
    if (seat.seatId) {
      return {
        ...seat,
        direction: activeLeg === 0 ? 'D' : 'R',
      };
    }
    if (
      !seat.seatId &&
      sortedSeatsList.length > 1 &&
      sortedSeatsList[0].flightId !== seat.flightId
    ) {
      return {
        ...seat,
        direction: activeLeg === 0 ? 'D' : 'R',
      };
    } else {
      return seat;
    }
  });

  const {
    origin,
    departDate,
    returnDate,
    destination,
    adultsCount,
    childrenCount,
    lapInfantCount,
    infantCount,
    tripType,
  } = flightSearchCriteria;

  const totalPaxCount =
    adultsCount + childrenCount + lapInfantCount + infantCount;

  const rawProducts = [];

  seatDirectionList.forEach(seat => {
    let seatName = '';

    if (seat.seatId) {
      seatName = getSeatName(
        getSeatMap(seat.direction, seatMaps),
        Number(seat.seatId.match(/\d+/)[0]),
        seat.seatId.replace(/[^a-z]/gi, '')
      );
    } else {
      const direction = activeLeg === 0 ? 'D' : 'R';
      seatName = getSeatName(
        getSeatMap(direction, seatMaps),
        seat.row,
        seat.column
      );
    }

    seatName = seatName === ALLEGIANT_FIRST_BUNDLE ? ALLEGIANT_FIRST : seatName;

    const seatNumber = seat.seatId ? seat.seatId : seat.row + seat.column;

    const flightNumber = seat.flightId.split('-G4-')[1];

    const flightDate = seat.flightId.split('-G4-')[0];

    const price = seat?.bundledAncillaryPrice?.amount
      ? seat?.bundledAncillaryPrice?.amount
      : seat.price.amount;
    const bookingDaysOut = moment(departDate?.date).diff(moment(), 'days');

    const direction = seat.direction
      ? seat.direction
      : activeLeg === 0
      ? 'D'
      : 'R';

    const route =
      seat.direction === 'D'
        ? origin + destination
        : seat.direction === 'R'
        ? destination + origin
        : activeLeg === 0
        ? origin + destination
        : destination + origin;

    const itemId =
      route +
      '-' +
      flightNumber +
      '-' +
      seatNumber +
      '-' +
      flightDate +
      '-' +
      direction;

    const product = {
      item_id: itemId,
      item_name: seatName + '-' + seatNumber,
      coupon: '',
      discount: '',
      affiliation: 'WWW - Booking',
      index: seatNumber,
      item_brand: (operatedBy && operatedBy.carrier) || 'G4',
      item_category: 'Seats',
      item_category2: seatName,
      item_category3: direction,
      item_category4: route,
      item_category5: affinityString,
      item_list_id: itemId,
      price: price,
      item_variant: `${seatName}-${seatNumber}-${route}-${flightNumber}-${flightDate}`,
      quantity: 1,
      item_promotion_name: '',
      item_promotion_id: '',
      item_partial_reservation: '',
      item_loyalty_points_discount: '',
      item_flight_booking_type: tripType,
      item_booking_start: departDate?.date,
      item_booking_end:
        tripType === tripTypeValues.ROUNDTRIP
          ? returnDate?.date
          : departDate?.date,
      item_booking_days_out: bookingDaysOut,
      item_count_child: childrenCount,
      item_count_lap_child: lapInfantCount,
      item_count_infant: infantCount,
      item_count_adult: adultsCount,
      item_count_total: totalPaxCount,
      item_credit_applied: '',
      item_flight_voucher_discount: 0,
      item_round_trip_discount: 0,
    };
    rawProducts.push(product);
  });
  return rawProducts;
};

const trackSeatSelection = (
  newSelections,
  oldSelections,
  flightSearchCriteria,
  activeLeg,
  seatMaps,
  operatedBy,
  autoAddedPriority,
  flightInfo,
  affinityString,
  oldPBSelections
) => {
  const seatsToAdd = newSelections.filter(
    seat =>
      !oldSelections?.find(
        initialSeat =>
          initialSeat.row === seat.row && initialSeat.column === seat.column
      )
  );

  const seatsToRemove = oldSelections?.filter(
    initialSeat =>
      !newSelections.find(
        seat =>
          initialSeat.row === seat.row && initialSeat.column === seat.column
      )
  );

  const affinityStringForSeatsToAdd = affinityString.includes('Seats')
    ? affinityString
    : `${affinityString}, Seats`;
  const ancillariesToAdd = autoAddedPriority.filter(ancillary => {
    return flightInfo[activeLeg].flight.id === ancillary.flightId;
  });

  const ancillariesToRemove = oldPBSelections.filter(ancillary => {
    return seatsToRemove.find(seat => ancillary.flightId === seat.flightId);
  });

  const seatsToAddRawProducts = getRawProducts(
    seatsToAdd,
    flightSearchCriteria,
    activeLeg,
    seatMaps,
    operatedBy,
    affinityStringForSeatsToAdd
  );

  const seatsToRemoveRawProducts = getRawProducts(
    seatsToRemove,
    flightSearchCriteria,
    activeLeg,
    seatMaps,
    operatedBy,
    affinityString
  );

  const seatsToAddImpressionData = {
    event: 'add_to_cart',
    curency: 'USD',
    value: '',
    name: sortOrder.DEFAULT,
    products: seatsToAddRawProducts,
  };

  const seatsToRemoveImpressionData = {
    event: 'remove_from_cart',
    curency: 'USD',
    value: '',
    products: seatsToRemoveRawProducts,
  };

  const checkoutPayload = {
    event: 'begin_checkout',
    currency: 'USD',
    price: '',
    products: seatsToAddRawProducts,
  };

  const getAncillariesRawProducts = (
    ancillariesList,
    flightsInfo,
    searchCriteria,
    bundle,
    price,
    operatedBy
  ) => {
    const rawProducts = [];
    let index = 1;

    ancillariesList.forEach(ancillary => {
      const ancillaryType = ancillary.ancillaryType || ancillary.type;
      const itemsIncludedInBundle = bundle?.ancillaries?.find(
        bundleAncillary => bundleAncillary.type === ancillaryType
      ).numberOfIncludedItems;

      const quantity =
        ancillary.isBundledAncillaryIncluded && itemsIncludedInBundle
          ? ancillary.quantity - itemsIncludedInBundle
          : ancillary.quantity;

      const itemPrice = price ? price : ancillary.price.amount;

      const flightInfo = flightsInfo.find(
        flight => flight.flight.id === ancillary.flightId
      );

      rawProducts.push(
        getAncillaryRawProduct(
          ancillaryType,
          flightInfo,
          operatedBy,
          index,
          quantity,
          itemPrice,
          searchCriteria,
          affinityString
        )
      );
      index++;
    });

    return rawProducts;
  };

  if (ancillariesToRemove && ancillariesToRemove.length) {
    const rawProductsToAdd = getAncillariesRawProducts(
      ancillariesToRemove,
      flightInfo,
      flightSearchCriteria,
      null,
      0,
      operatedBy
    );

    seatsToRemoveImpressionData['products'] = [
      ...seatsToRemoveImpressionData.products,
      ...rawProductsToAdd,
    ];
  }
  if (ancillariesToAdd && ancillariesToAdd.length) {
    const rawProductsToAdd = getAncillariesRawProducts(
      ancillariesToAdd,
      flightInfo,
      flightSearchCriteria,
      null,
      0,
      operatedBy
    );

    seatsToAddImpressionData['products'] = [
      ...seatsToAddImpressionData.products,
      ...rawProductsToAdd,
    ];
    checkoutPayload['products'] = [
      ...checkoutPayload.products,
      ...rawProductsToAdd,
    ];
  }

  tracking.checkout(checkoutPayload);
  seatsToAddRawProducts.length && tracking.addToCart(seatsToAddImpressionData);
  seatsToRemoveRawProducts.length &&
    tracking.removeFromCart(seatsToRemoveImpressionData);
};

export const trackDeselectSeatEvent = () => {
  tracking.trackEvent(
    'site_click_tracking',
    'Seat Chooser',
    'Deselect My Seats for Me',
    null
  );
};

export const trackSelectSeatEvent = () => {
  tracking.trackEvent(
    'site_click_tracking',
    'Seat Chooser',
    'Select My Seats for Me',
    null
  );
};

export const trackDeclineSeatSelection = (event, route) => {
  tracking.trackEvent({
    event: event,
    route: route,
  });
};

export default trackSeatSelection;
